import React, { useCallback } from 'react'
import { Control, Controller, DeepMap, FieldError, useForm, UseFormSetValue, UseFormWatch } from 'react-hook-form'
import { CreateRoomMaintenanceForm, CreateRoomMaintenanceSchema } from '../../../../services/room-maintenance/schema/CreateRoomMaintenanceSchema'
import { zodResolver } from '@hookform/resolvers/zod'
import useCheckAvi from '../../../../services/booking/hooks/useCheckAvi'
import Select from 'react-select'
import clsx from 'clsx'
import { useEffect, useState } from 'react'
import { CheckAviResponseType, RoomTypeData } from '../../../../services/booking/response/CheckAviResponse'
import { toast } from 'sonner'
import { useQueryClient } from '@tanstack/react-query'
import { useCreateRoomMaintenance } from '../../../../services/room-maintenance/hooks/useCreateRoomMaintenance'
import DateInput from '../../../../shared/components/form/DateInput'

interface CreateRoomMaintenanceProps {
    onClose: () => void,
    startDate?: string,
    endDate?: string,
    roomTypeId?: string,
    roomId?: string
}

const CreateRoomMaintenanceComponent: React.FC<CreateRoomMaintenanceProps> = ({ onClose, startDate, endDate, roomTypeId, roomId }) => {
    const { register, handleSubmit, control, setValue, reset, formState: { errors }, watch, setError } = useForm<CreateRoomMaintenanceForm>({
        resolver: zodResolver(CreateRoomMaintenanceSchema),
        defaultValues: {
            checkin_date: startDate,
            checkout_date: endDate,
            room_type_id: roomTypeId,
            room_id: roomId,
        }
    })

    const { data: roomAvi, isFetching, isError, error, isSuccess } = useCheckAvi({
        checkin_date: watch('checkin_date'),
        checkout_date: watch('checkout_date')
    });

    const handleClose = () => {
        reset()
        onClose()
    }

    const { mutate, isPending } = useCreateRoomMaintenance();

    const queryClient = useQueryClient();

    const onSubmit = useCallback((values: CreateRoomMaintenanceForm) => {
        toast.loading('Creating room maintenance...', { id: 'create-room-maintenance' });

        mutate(values, {
            onSuccess: async () => {
                handleClose();

                await queryClient.refetchQueries({
                    predicate: (query) => query.queryKey.some((key) => ['bookings'].includes(key as string))
                });

                toast.success('Room maintenance created successfully', { id: 'create-room-maintenance' });
            },
            onError: (mutationError) => {
                if ((mutationError as any)?.status === 422) {
                    const validationErrors = (mutationError as any)?.response?.data?.errors;
                    if (validationErrors) {
                        for (const [field, messages] of Object.entries(validationErrors)) {
                            setError(field as keyof CreateRoomMaintenanceForm, {
                                type: "manual",
                                message: (messages as string[])[0],
                            });
                        }
                        toast.error('Failed to create room maintenance', { id: 'create-room-maintenance' });
                    }
                } else {
                    toast.error(mutationError?.message || 'Failed to create room maintenance', { id: 'create-room-maintenance' });
                }
            }
        });
    }, [mutate])

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <div className="row mb-3 align-items-start">
                <div className="col-sm-3">
                    <label htmlFor="checkin_date" className="col-sm-4 col-form-label d-flex" style={{ fontSize: '14px' }}>Checkin Date:</label>
                    <DateInput
                        style={{ fontSize: '14px', height: '35px' }}
                        {...register('checkin_date')}
                    />
                    {errors.checkin_date && <span className="text-danger">{errors.checkin_date.message}</span>}
                </div>
                <div className="col-sm-3">
                    <label htmlFor="checkout_date" className="col-sm-4 col-form-label d-flex" style={{ fontSize: '14px' }}>Checkout Date:</label>
                    <DateInput
                        style={{ fontSize: '14px', height: '35px' }}
                        {...register('checkout_date')}
                    />
                    {errors.checkout_date && <span className="text-danger">{errors.checkout_date.message}</span>}
                </div>
                {/* room type id */}
                <div className="col-sm-3">
                    {!isSuccess && (
                        <div className="col-12">
                            <label className="col-form-label" htmlFor="room">Room:</label>
                            <Select
                                options={[]}
                                isDisabled={true}
                                placeholder="Select checkin and checkout date"
                            />
                        </div>
                    )}
                    {isError && <span className="text-danger">{error?.message}</span>}
                    {isFetching && (
                        <div className="col-12">
                            <label className="col-form-label" htmlFor="room">Room:</label>
                            <Select
                                options={[]}
                                isLoading={true}
                                isDisabled={true}
                                placeholder="Loading..."
                            />
                        </div>
                    )}
                    {isSuccess && !isFetching && (
                        <SelectRoomType control={control} roomType={roomAvi} setValue={setValue} errors={errors} />
                    )}
                </div>
                {/* room id */}
                <div className="col-sm-3">
                    {watch('room_type_id') === undefined && (
                        <div className="col-12">
                            <label className="col-form-label" htmlFor="subroom">Subroom:</label>
                            <Select
                                options={[]}
                                isDisabled={true}
                                placeholder="Select room first"
                            />
                        </div>
                    )}
                    {isSuccess && !isFetching && watch('room_type_id') && (
                        <SubroomComponent watch={watch} control={control} index={0} roomType={roomAvi} errors={errors} />
                    )}
                </div>
            </div>
            <div className="row mb-3 align-items-start">
                <div className="col-sm-12">
                    <label htmlFor="note" className="col-sm-2 col-form-label d-flex" style={{ fontSize: '14px' }}>Note:</label>
                    <textarea
                        className="form-control app-form-control rounded-1"
                        style={{ fontSize: '14px' }}
                        placeholder="Note"
                        {...register('note')}
                    />
                    {errors.note && <span className="text-danger">{errors.note.message}</span>}
                </div>
            </div>
            <div className="my-4 border-bottom"></div>
            <div className="d-flex flex-column justify-content-end gap-2">
                <div className="d-flex justify-content-between gap-2">
                    <button type="button" className="btn btn-secondary" onClick={() => handleClose()}>
                        Cancel
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={isPending}>
                        Create Room Maintenance
                    </button>
                </div>
            </div>
        </form>
    )
}

export default CreateRoomMaintenanceComponent

interface SelectRoomTypeProps {
    control: Control<any>
    roomType: CheckAviResponseType
    setValue: UseFormSetValue<any>
    errors: DeepMap<any, FieldError>
}

interface RoomType {
    value: string,
    label: string
}

const SelectRoomType: React.FC<SelectRoomTypeProps> = ({ control, roomType, setValue, errors }) => {
    const [roomTypeOptions, setRoomTypeOptions] = useState<RoomType[]>([]);

    useEffect(() => {
        if (roomType.data.length > 0) {
            setRoomTypeOptions(roomType.data.map((room: { id: string, name: string }) => ({
                value: room.id,
                label: room.name
            })))
        }
    }, [roomType])

    return (
        <div className="col-12">
            <label className="col-form-label" htmlFor="room">Room:</label>
            <Controller
                control={control}
                name={'room_type_id'}
                render={({ field }) => (
                    <Select
                        className={clsx('', { 'is-invalid': errors.room_type_id })}
                        options={roomTypeOptions}
                        value={roomTypeOptions.find((room) => room.value === field.value)}
                        onChange={(selectedOption) => {
                            if (selectedOption) {
                                field.onChange(selectedOption.value);
                                setValue(`room_id`, '');
                            }
                        }}
                        noOptionsMessage={() => 'No rooms available'}
                    />
                )}
            />
            {errors.room_type_id && (
                <div className="invalid-feedback">
                    {errors.room_type_id.message}
                </div>
            )}
        </div>
    )
}

interface SubroomComponentProps {
    watch: UseFormWatch<any>,
    control: Control<any>,
    index: number,
    roomType: CheckAviResponseType,
    errors: DeepMap<any, FieldError>
}

interface Subroom {
    value: string,
    label: string
}

const SubroomComponent: React.FC<SubroomComponentProps> = ({ watch, control, roomType, errors }) => {
    const [subroomOptions, setSubroomOptions] = useState<Subroom[]>([]);

    useEffect(() => {
        const roomTypeId = watch(`room_type_id`);
        if (roomTypeId) {
            const roomData = roomType.data.find((room: RoomTypeData) => room.id === roomTypeId);
            if (roomData) {
                let rooms = roomData.rooms?.map((room: { id: string, room_number: string }) => ({
                    value: room.id,
                    label: room.room_number
                })) || [];

                // Jika tidak ada room, tambahkan opsi "Unassigned Room"
                if (rooms.length === 0) {
                    rooms = [{ value: '', label: "Unassigned Room" }];
                }

                setSubroomOptions(rooms);
            }
        }
    }, [watch(`room_type_id`)]);

    return (
        <div className="col-12">
            <label className="col-form-label" htmlFor="subroom">Subroom:</label>
            <Controller
                control={control}
                name={`room_id`}
                render={({ field }) => (
                    <Select
                        className={clsx('', { 'is-invalid': errors.room_id })}
                        options={subroomOptions}
                        value={subroomOptions.find((room) => room.value === field.value) || null}
                        onChange={(selectedOption) => {
                            field.onChange(selectedOption?.value ?? null);
                        }}
                        noOptionsMessage={() => 'No rooms available'}
                    />
                )}
            />
            {errors.room_id && (
                <div className="invalid-feedback">
                    {errors.room_id.message}
                </div>
            )}
        </div>
    );
};